import { motion } from 'framer-motion';
import { Star, ExternalLink } from 'lucide-react';

export type LiveGoogleReview = {
  id: string;
  user: string;
  rating: number;
  comment: string;
  date: string;
};

type Props = {
  reviews: LiveGoogleReview[];
  reviewUrl?: string;
};

function GoogleGIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export function GoogleReviewsLiveWidget({ reviews, reviewUrl }: Props) {
  if (reviews.length === 0) {
    return (
      <div className="py-16 text-center bg-gray-50 rounded-[2rem] border border-dashed border-gray-200">
        <GoogleGIcon className="w-10 h-10 mx-auto mb-4" />
        <p className="text-gray-600 font-semibold">Avis Google bientôt disponibles</p>
        {reviewUrl && (
          <a
            href={reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-sm font-bold text-[#ff033c] hover:underline"
          >
            Soyez le premier à laisser un avis
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    );
  }

  const avg =
    reviews.reduce((sum, r) => sum + r.rating, 0) / Math.max(reviews.length, 1);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4 px-2">
        <div className="flex items-center gap-3">
          <GoogleGIcon className="w-8 h-8" />
          <div>
            <p className="text-sm font-black text-gray-900">Google Reviews</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex text-yellow-400">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i <= Math.round(avg) ? 'fill-yellow-400' : 'text-gray-200'}
                  />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-700">{avg.toFixed(1)} / 5</span>
              <span className="text-xs text-gray-400">({reviews.length} avis)</span>
            </div>
          </div>
        </div>
        {reviewUrl && (
          <a
            href={reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-950 text-white text-sm font-bold hover:bg-[#ff033c] transition-colors"
          >
            Laisser un avis Google
            <ExternalLink size={14} />
          </a>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.map((review, index) => (
          <motion.article
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.08 }}
            className="bg-white p-7 rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex text-yellow-400">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i <= review.rating ? 'fill-yellow-400' : 'text-gray-200'}
                  />
                ))}
              </div>
              <GoogleGIcon className="w-4 h-4 opacity-80" />
            </div>
            <p className="text-gray-600 leading-relaxed mb-6 text-sm italic line-clamp-5">
              &quot;{review.comment}&quot;
            </p>
            <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff033c] to-gray-900 text-white flex items-center justify-center font-black uppercase text-sm">
                {review.user.charAt(0)}
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm leading-none">{review.user}</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Avis Google</p>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  );
}
